// searchWire.js — THE request a Desk search sends (moved verbatim from src/db/core.js, F-104, 2026-09-23).
//
// One function turns the committed filter state into the query-string pairs the agent-listings route
// receives: the registry rows (wireParams) AND the hand-written lines the registry cannot express yet
// (deal, kind, rooms/bedrooms envelopes and num_sets, text, near, move-in, the legacy date trio…).
// It lives here, with no dependencies beyond the registry, so the cache key (queryKey.js) can be built
// from exactly this output. Keying on the registry half alone let a land search and an apartment
// search share one key — F-104. core.js imports and re-exports it; nothing else changed.
import { wireParams, hasDateRanges } from './deskFilters.js'
import { searchedDistricts } from './districtFilter.js'

export function applySearchFilterParams(q, filters, { scope, unknownLimit, unknownOffset, sort, since } = {}) {
    if (filters) {
        const f = filters
        // Checkbox facets (2026-07-17): intents/kinds are multi-select arrays. ONE pick keeps the
        // scalar `intent`/`kind` every server version reads. 2026-10-23 (audit B04, core 3832): a
        // multi-pick now travels too, as `intents`/`kinds`, so the server pages, sorts and counts
        // the exact set instead of a widened fetch the browser then thinned. Separate keys on
        // purpose: a server without the change ignores an unknown key and answers the old wide
        // way (the client-side cut — classify, multiPickClusterCut — still enforces the set on
        // what it loads), whereas `kind=a,b` would read as one unknown kind and match nothing.
        // Rooms/bedrooms sets: the RPC speaks min/max, so ship the set's [lo,hi] envelope
        // (5 = the "5+" box → open top) plus num_sets for a gapped pick (F-090, below).
        if (f.intents?.length === 1) q.set('intent', f.intents[0])
        if (f.kinds?.length === 1) q.set('kind', f.kinds[0])
        if (f.intents?.length > 1) q.set('intents', [...f.intents].sort().join(','))   // sorted: one set, one cache key
        if (f.kinds?.length > 1) q.set('kinds', [...f.kinds].sort().join(','))
        // B265 (0359/v31): subtype tokens, CSV. Unlike kind (scalar param, single-select
        // pushdown only), subtypes is a real array param server-side — send them all.
        if (f.subtypes?.length) q.set('subtypes', f.subtypes.join(','))
        const env = set => (set?.length ? [Math.min(...set), set.includes(5) ? '' : Math.max(...set)] : ['', ''])
        const [roomsLo, roomsHi] = env(f.roomsSet), [bedsLo, bedsHi] = env(f.bedroomsSet)
        // F-090 (owner, 2026-09-08): a selection with a GAP is not a range. Picking 1 and 3 also returned 2 --
        // 16 of the 31 selections this five-button control can make were wrong -- because env() above collapses
        // the set to a min/max envelope and the wire carried nothing else. The envelope STILL goes (the candidate
        // pre-cut and facts_of read it, and an envelope over-includes, which is sound); the set goes too, and ONLY
        // when the selection is non-contiguous, so every contiguous search keeps the exact request it sends today.
        // 5 is the control's open-ended top button, so it travels as gte rather than as a member.
        const gapped = set => {
          const v = [...new Set((set || []).map(Number))].filter(Number.isFinite).sort((x, y) => x - y)
          return v.length > 1 && v.some((x, i) => i && x !== v[i - 1] + 1) ? v : null
        }
        const numSets = {}
        for (const [key, set] of [['rooms', f.roomsSet], ['bedrooms', f.bedroomsSet]]) {
          const v = gapped(set); if (!v) continue
          const OPEN = 5
          numSets[key] = v.includes(OPEN) ? { in: v.filter(x => x < OPEN), gte: OPEN } : { in: v }
        }
        if (Object.keys(numSets).length) q.set('num_sets', JSON.stringify(numSets))
        // Multi-district (2026-07-16): CSV, OR semantics server-side (migration 0100).
        if (f.districtCodes?.length) q.set('district_codes', searchedDistricts(f.districtCodes).join(','))   // Gori town = its municipality
        // 0363/0372: 'Saburtalo and NOTHING under it'. A SECOND array rather than a mode flag —
        // a flag could not express 'Vake with its neighbourhoods, OR Saburtalo exactly', and the
        // two arrays simply OR together in the RPC's allowed set.
        if (f.districtOwnCodes?.length) q.set('district_own_codes', f.districtOwnCodes.join(','))
        // 0537/0538: street UUIDs, OR semantics, and the server validates them as UUIDs
        // before they reach SQL. A street used to be a ~700m circle around a single point;
        // it is a stored link now, so this is a selection like any other.
        if (f.streetIds?.length) q.set('streets', f.streetIds.join(','))
        // 0729: a development as an identity filter (p_complex_ids in v56). Ids only —
        // a complex NAME must never travel as text: no recognizer claims it, so it fell
        // through to an 11s full-corpus scan that matched nothing (Georgian adverts write
        // ჰუალინგი; the row says "Hualing Tbilisi Sea New City").
        if (f.complexIds?.length) q.set('complexes', f.complexIds.join(','))
        // 0688/v48: metro stations, the street rail's twin. Same OR semantics, same
        // server-side UUID validation, same three-valued verdict — a property whose ad
        // never mentions a metro lands on the possible-matches shelf, never dropped.
        if (f.metroIds?.length) q.set('metro', f.metroIds.join(','))
        // Q4 nearby opt-in (0541/0543). Only meaningful WITH a street selected — the radius is
        // measured from the selected street's centroid — so it is sent only then; alone it
        // would be a parameter the RPC ignores and a checkbox that appears to do nothing.
        // ⚠️ NOT a filter: it does not narrow the result, it asks for an EXTRA group. That is
        // why it lives outside `advFiltersActive` and why the server keeps it out of the
        // filter registry (a registry entry would trip `isFiltered` for a request that
        // filters nothing).
        // ⚠️ AND ONLY ON A REQUEST THAT IS ALREADY FETCHING SHELF ROWS (`unknownLimit !== 0`).
        // The nearby rows are hydrated server-side through the SAME per-row batches as the
        // shelf (props, authors, cooperations, terms, thumbnails), and `unknown_limit=0` is
        // precisely the 2026-08-10 change that took those batches off the critical path —
        // measured 738KB→231KB and 6.4s→3.9s cold. `nearby_ids` is NOT sliced by
        // p_unknown_limit (it is derived from the nearby CTE, not the shelf page), so asking
        // for it here would put up to a few hundred properties of hydration straight back
        // into the request the matched cards are waiting on. It rides the shelf request
        // instead, a beat later, exactly as the shelf itself now does.
        // …and only on the shelf's FIRST page. `nearby_ids` is the whole nearby set every
        // time it is asked for — it has no cursor — so a shelf pager that kept asking would
        // re-hydrate and re-send the same few hundred rows on every "load 60 more" click.
        if (f.streetIds?.length && f.streetNearbyM && unknownLimit !== 0 && !unknownOffset) q.set('streets_near', String(f.streetNearbyM))
        const numPairs = [['price_min', f.priceMin], ['price_max', f.priceMax],
            ['rooms_min', roomsLo], ['rooms_max', roomsHi],
            ['bedrooms_min', bedsLo], ['bedrooms_max', bedsHi],
            ['floor_min', f.floorMin], ['floor_max', f.floorMax],
            ['area_min', f.areaMin], ['area_max', f.areaMax],
            // v29 (0345): LAND area — the plot, distinct from area_min/max, which is the
            // BUILDING. Sent only when set; absent params are null server-side, so a client
            // that never sets them behaves exactly as before.
            ['land_area_min', f.landAreaMin], ['land_area_max', f.landAreaMax],
            // v6 (0105, 2026-07-17, audit #8): bathrooms/floorsTotal were the last two panel
            // filters that never reached the server — client-only since 0048, which meant they
            // only ever saw loaded pages once demand-driven loading shipped (2026-07-15), and a
            // fetch ran fully unfiltered whenever either was the ONLY active constraint.
            ['bathrooms_min', f.bathroomsMin], ['bathrooms_max', f.bathroomsMax],
            // 0757 (v60): balcony COUNT. Idmon reads it from the advert prose; 28,359
            // properties carry it and nothing could ask for it until now.
            ['balcony_count_min', f.balconyCountMin], ['balcony_count_max', f.balconyCountMax],
            ['floors_total_min', f.floorsTotalMin], ['floors_total_max', f.floorsTotalMax]]
        for (const [k, v] of numPairs) if (v !== '' && v != null) q.set(k, v)
        // v11 (0120, 2026-07-18): top floor — boolean cross-column predicate (floor ==
        // floors_total), from the compiler's "ბოლო სართული"/"N/N" understanding (engine v15).
        if (f.topFloor) q.set('top_floor', '1')
        // owner 2026-09-05: first floor + the ✕ halves of both flags (spec: topFloorNot / firstFloorNot)
        if (f.firstFloor) q.set('first_floor', '1')
        if (f.topFloorNot) q.set('top_floor_not', '1')
        if (f.firstFloorNot) q.set('first_floor_not', '1')
        // 0190 (2026-07-23): portal source filter — canonical listing.source values CSV
        // (SOURCE_FACET_OPTIONS in IntakeTab.jsx holds exactly the server's whitelist).
        // ALL SCOPES since 2026-08-22 (B915). This used to be leads-only, and the comment
        // that stood here explained why: "the server would ignore the values but still
        // flip into filtered/RPC mode for a no-op — so don't even send it there". That
        // was true and is not any more; search_filters.js now emits p_sources on every
        // scope, and every live search version since v40 honours it.
        // ⚠️ BOTH halves were leads-gated, and fixing only the server left the bug fully
        // intact — the browser simply never sent the parameter, so ticking Korter on
        // browse still returned the whole corpus. If a filter looks dead, check that the
        // client SENDS it before concluding anything about the server.
        if (f.sources?.length) q.set('sources', f.sources.join(','))
        // agentIds (2026-07-28): admin "filter by agent", pushed down. The server narrows
        // the PARTY SET behind p_party_ids, which already means "whose work is this" — so
        // this filters the whole corpus in SQL instead of the loaded pages the client-side
        // name matcher could see. scope=all only: on scope=mine the party set IS the agent.
        if (scope === 'all' && f.agentIds?.length) q.set('agent_ids', f.agentIds.join(','))
        // restrictions (0244/0245, 2026-07-29): "<pol_ code>:<hide|allow|restricted>" CSV,
        // answered by RPC v16 over the WHOLE corpus. This was a loaded-rows-only control
        // until now, which the 2026-07-28 ruling turned from merely imprecise into wrong:
        // the lens became "find the confirmed few" (12 listings among ~9,000), so the chip
        // rendered AI drafts and archived rows while its own label still said "from N
        // loaded". The MODE IS THE SERVER'S POLICY KEY — what each mode means lives in the
        // versioned search_policy table, so nothing here re-implements it, and a future
        // ruling is a row rather than a client deploy.
        if (f.restrictions?.length) q.set('restrictions', f.restrictions.join(','))
        // features (B37, 2026-07-29): bare `feature.code` CSV, AND-ed, answered by
        // p_feature_codes — live in the RPC since 0241 and driven by nothing until now,
        // deliberately: a param no control can represent is a dead control. Three verdicts,
        // so the shelf matters here more than anywhere else — property_feature records
        // "someone looked and there is no elevator" (mismatch) distinctly from "nobody ever
        // recorded one" (unknown), and only 22.6k of the corpus's properties carry any row
        // at all. The client sends the codes and reads the split; it never evaluates them.
        if (f.featureCodes?.length) q.set('features', f.featureCodes.join(','))
        // 0756/v59: the SAME features asked with a polarity, "<code>:<yes|no|unknown>".
        // Separate param, never folded into `features`: that one means "true" and the
        // materialiser and older clients still send it, so folding would change what an
        // existing caller means. They compose — the RPC ANDs them.
        if (f.featureStates?.length) q.set('feature_states', f.featureStates.join(','))
        // condition (0433, v37): the state of the flat — რემონტი and კარკასი. A SEPARATE
        // param from `features` on purpose. Both are `feature.code` CSVs, but the server
        // ANDs `features` ("balcony and lift") and ORs `condition` ("new renovation or
        // renovated", including across the two ladders). One param cannot mean both.
        // The three verdicts differ too, and the difference is the whole filter: a property
        // carrying ANY rung of a ladder is KNOWN for that ladder, so a flat recorded
        // ძველი რემონტი MISMATCHES ახალი რემონტი rather than shelving as unknown. Only a
        // property with no rung at all reaches the possible-matches shelf. The client sends
        // codes and reads the split; it never evaluates them.
        if (f.conditionCodes?.length) q.set('condition', f.conditionCodes.join(','))
        // ── REGISTRY-DRIVEN TRANSPORT ───────────────────────────────────────────────
        // Every registered filter's wire parameter, for anything not already sent by hand
        // above. This is the last of the six sites: with it, ONE row in deskFilters.js now
        // reaches the state defaults, the array semantics, the committed set, the server-only
        // set, both halves of the URL, and the request.
        // ⚠️ `q.has` IS THE SKIP, and it must stay: a hand-written line above may deliberately
        // send a DIFFERENT shape than the generic one (streets, for instance, is conditional on
        // the nearby opt-in). Overwriting it here would silently replace a considered rule with
        // a default one — and both would look correct in the query string.
        // 🚨 B1208 (2026-09-04): THE ENCODING IS THE REGISTRY'S, NOT THIS LOOP'S. The loop that
        // stood here did `String(v)` for anything that was not an array, and the B1188 date
        // ranges are an OBJECT whose default is {} -- so every request whose dateRanges was
        // untouched carried `dates=[object Object]`, the server's fail-closed parse refused the
        // whole search, and every filtered Desk search answered zero from 09:00 until this fix
        // (600+ rejections in search_exec_log). A hand-written `if (hasDateRanges) q.set('dates',
        // JSON…)` sat above the loop and covered only the NON-empty case; the empty one fell
        // through to String(). The rule that fixes the class: a value's wire form is declared ON
        // ITS ENTRY (`wireEncode`), an unset filter (`isSet`) never travels, and an object with
        // no encoder THROWS in the suite instead of reaching a request. wireParams() is pure and
        // tested. Requirements, scopes and enumerated values are honoured inside it, as before.
        // ⚠️ AND THE LEGACY DATE TRIO STAYS SUPPRESSED below: 0863's CASE tests p_dates IS NOT
        // NULL first, so a request carrying both would silently ignore date_field/from/to.
        for (const [pk, v] of wireParams(f, scope)) {
            // `q.has` IS THE SKIP, and it must stay: a hand-written line above may deliberately
            // send a DIFFERENT shape than the generic one (streets, conditional on the nearby
            // opt-in). Overwriting it here would silently replace a considered rule.
            if (!q.has(pk)) q.set(pk, v)
        }
        // 0249 (2026-07-29): date range over ONE chosen dimension, whole-corpus in SQL.
        // dateField is sent only alongside a bound — alone it selects nothing and would
        // flip the fetch into filtered/RPC mode for a no-op (the same rule `currency`
        // follows two lines down).
        if ((f.dateFrom || f.dateTo) && !hasDateRanges(f.dateRanges)) {
            q.set('date_field', f.dateField || 'added')
            if (f.dateFrom) q.set('date_from', f.dateFrom)
            if (f.dateTo) q.set('date_to', f.dateTo)
        }
        // Assigned agents (live lead claims) + view status. ⚠️ `!= null && !== ''` and NOT
        // truthiness: claimsLt = 0 is the owner's "None" case and is the single most likely
        // value an agent types. A falsiness check here would silently drop exactly it.
        for (const [k, v] of [['claims_lt', f.claimsLt], ['claims_gt', f.claimsGt]]) {
            if (v !== '' && v != null) q.set(k, v)
        }
        // 0253: four modes. The _by_me pair carries no viewer id — the server resolves that
        // from the verified session, because a client-named viewer would let one agent
        // filter by a COLLEAGUE'S view history.
        if (['seen', 'unseen', 'seen_by_me', 'unseen_by_me'].includes(f.seen)) q.set('seen', f.seen)
        // B215 (0262): exposure-ladder threshold. Same non-falsiness guard as claims —
        // '0' ("untouched only") is the single most useful value.
        if ((f.exposureMax ?? '') !== '') q.set('exposure_max', f.exposureMax)
        // 0430/0431: the "…except mine" modifier. The server turns this FLAG into the
        // VERIFIED viewer's party id — a client-supplied id would let one agent ask to keep
        // another agent's leads, which is why only a '1' travels.
        if (f.keepMine) q.set('exposure_keep_mine', '1')
        // B213 (0263): market-spread band token; the server maps it to p_spread_min/max.
        if (f.spread) q.set('spread', f.spread)
        // 0294 (v22) — the hunter lenses. sellerType is a band token like spread (the
        // server owns the detents, so they retune without a function version). The two age
        // bounds and viewsMin keep the same non-falsiness guard as claims/exposure above:
        // ageMinDays 0 ("posted today") and viewsMin 0 ("nobody has looked") are both real
        // values that a truthiness check would silently drop.
        if (f.sellerType) q.set('seller_type', f.sellerType)
        if ((f.ageMinDays ?? '') !== '') q.set('age_min_days', f.ageMinDays)
        if ((f.ageMaxDays ?? '') !== '') q.set('age_max_days', f.ageMaxDays)
        // The kind rides only alongside a range — alone it is a modifier with nothing to
        // modify, and sending it would route an unfiltered feed through the search RPC.
        if ((f.ageKinds || []).length && ((f.ageMinDays ?? '') !== '' || (f.ageMaxDays ?? '') !== ''))
            q.set('age_kinds', f.ageKinds.join(','))
        if ((f.viewsMin ?? '') !== '') q.set('views_min', f.viewsMin)
        if (f.promoted) q.set('promoted', f.promoted)
        if ((f.footprint || []).length) q.set('footprint', f.footprint.join(','))
        if (f.ownerEngaged) q.set('owner_engaged', f.ownerEngaged)
        // 0303 band tokens (spread's replacements); '0' is a non-empty string, truthiness safe.
        if (f.reposts) q.set('reposts', f.reposts)
        if (f.ownerPlatforms) q.set('owner_platforms', f.ownerPlatforms)
        // Advert quality (0702-0704, v55). Names must match search_filters.js exactly —
        // the server registry is the ONE declaration and an unknown key is ignored in
        // silence, which is the failure mode B261 exists to prevent.
        if (f.photos) q.set('photos', f.photos)
        if (f.noDescription) q.set('no_description', '1')
        if (f.missingFacts) q.set('missing_facts', '1')
        // B177 pushdown (2026-07-29): the payment ceiling and the move-in date, which the
        // RPC has accepted since v15 and this layer never sent. moveInHard rides only
        // alongside a date — alone it is a modifier with nothing to modify.
        if ((f.advanceMaxMonths ?? '') !== '') q.set('advance_max_months', f.advanceMaxMonths)
        if ((f.moveInBy ?? '') !== '') {
            q.set('move_in_by', f.moveInBy)
            if (f.moveInHard) q.set('move_in_hard', '1')
        }
        // currency only matters alongside a price bound — sending it alone would needlessly
        // route through the RPC (and the panel's currency toggle always has a value).
        // …and alongside a price-per-m² bound (core 3872), which is read in the same unit: without it a GEL band was
        // searched as dollars
        const set = v => v !== '' && v != null
        if (set(f.priceMin) || set(f.priceMax) || set(f.ppmMin) || set(f.ppmMax)) q.set('currency', f.currency || 'USD')
        // v5 (0104, 2026-07-16): residual free-text + near-location run server-side too.
        // ⚠️ OWNER RULING 2026-08-26: residual text is NOT a search predicate — "only the
        // chips the text resolves to." q was the p_text full-corpus scan: >40s on the leads
        // scope, and each such query held one of the server's FOUR search slots for its whole
        // run, which is how every other search queued behind it and the Desk read as hung.
        // Identity (phone / listing id / cadastral) never rode q — the reach-back owns it.
        //
        // ✅ RE-ENABLED 2026-08-26 (B938), owner-approved, because THAT PREMISE IS GONE.
        // The >40s was never the text matching. It was a missing CTE barrier: PostgreSQL
        // inlines a once-referenced CTE, and inlining `hay` flipped the join order of the
        // whole search query. Migrations 0737 (v57) and 0738 (v56, the version the deployed
        // server actually calls) add `hay as materialized`. Measured full-pool, leads/open:
        //   ჭავჭავაძის >100s → 6.1s   აღმაშენებლის >100s → 6.2s   ვაკე >100s → 12.4s
        // Proof the verdict function was NOT the cost: replacing it with a constant 'pass'
        // still measured 101,339 ms. See 0737's banner for the full attribution table.
        //
        // ⚠️ WHAT THE RULING STILL GOVERNS, and this line obeys: only `f.q` travels — the
        // RESIDUAL the compiler could not claim (B203) — never the raw box. A token that
        // became an intent/kind/street chip is already a predicate and must not be matched
        // as text as well; that double-counting is the zero-row bug B203 records.
        // ⚠️ STILL TRUE: a text query holds one of the server's FOUR search slots for its
        // whole run. 6s is not free — ვაკე is still 12.4s. If the Desk reads as hung under
        // concurrent searching again, this line is the first thing to re-measure, and the
        // remedy is the slot count or the ვაკე case, not silently dropping the predicate:
        // a search box that quietly returns the unfiltered pool is worse than a slow one.
        if (f.q) q.set('q', f.q)
        if (f.near?.lat != null && f.near?.lng != null) {
            q.set('near_lat', f.near.lat); q.set('near_lng', f.near.lng)
            q.set('near_radius_m', f.near.radiusM || 1000)
        }
        // v13 (0186, 2026-07-23): server-side sort — page SELECTION becomes whole-DB
        // truth (largest-area-first means largest in the database, not in loaded rows).
        // Only meaningful on the filtered path (the unfiltered feed RPC has no sort);
        // callers pass the mapped value (price_asc|price_desc|area_desc|updated).
        if (f.sort) q.set('sort', f.sort)
    }
}
